import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { catchError, firstValueFrom, Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { AuthService } from '../../../core/auth/auth.service';
import { AuthUtils } from '../../../core/auth/auth.utils';
import { BaseService } from '../../../core/services/base.service';
import { ApiResult } from '../../../shared/types/api-result.types';
import { AiAssistantModels, AiAssistantStreamEvent, AiConversation, AiConversationListItem } from './ai-chat.types';

const REFRESH_MARGIN_SECONDS = 30;

@Injectable({ providedIn: 'root' })
export class AiChatService extends BaseService {
    protected apiUrl = environment.apiUrl;

    private readonly _httpClient = inject(HttpClient);
    private readonly _authService = inject(AuthService);

    getModels(): Observable<ApiResult<AiAssistantModels>> {
        return this._httpClient
            .get<ApiResult<AiAssistantModels>>(`${this.apiUrl}backoffice/ai-assistant/models`)
            .pipe(catchError(this.handleError));
    }

    getConversations(): Observable<ApiResult<AiConversationListItem[]>> {
        return this._httpClient
            .get<ApiResult<AiConversationListItem[]>>(`${this.apiUrl}backoffice/ai-assistant/conversations`)
            .pipe(catchError(this.handleError));
    }

    getConversation(id: number): Observable<ApiResult<AiConversation>> {
        return this._httpClient
            .get<ApiResult<AiConversation>>(`${this.apiUrl}backoffice/ai-assistant/conversations/${id}`)
            .pipe(catchError(this.handleError));
    }

    deleteConversation(id: number): Observable<ApiResult<unknown>> {
        return this._httpClient
            .delete<ApiResult<unknown>>(`${this.apiUrl}backoffice/ai-assistant/conversations/${id}`)
            .pipe(catchError(this.handleError));
    }

    /**
     * Sends a prompt and reads the answer as a server-sent event stream. HttpClient buffers the
     * whole body, so this goes through fetch, adding the bearer token the interceptor would add.
     * Resolves when the stream ends; rejects on network/HTTP failures (not on `error` events).
     */
    async streamChat(
        payload: {
            conversationId: number | null;
            prompt?: string;
            approvals?: { requestId: string; approved: boolean }[];
            modelKey?: string;
        },
        onEvent: (event: AiAssistantStreamEvent) => void,
        signal: AbortSignal
    ): Promise<void> {
        const token = await this._validToken();
        const response = await fetch(`${this.apiUrl}backoffice/ai-assistant/chat`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify(payload),
            signal,
        });

        if (!response.ok || !response.body) {
            throw new Error(`HTTP ${response.status}`);
        }

        const reader = response.body.pipeThrough(new TextDecoderStream()).getReader();
        let buffer = '';

        while (true) {
            const { value, done } = await reader.read();
            if (done) {
                break;
            }

            buffer += value;
            let separator: number;
            while ((separator = buffer.indexOf('\n\n')) >= 0) {
                const block = buffer.slice(0, separator);
                buffer = buffer.slice(separator + 2);

                const data = block
                    .split('\n')
                    .filter((line) => line.startsWith('data:'))
                    .map((line) => line.slice(5).trimStart())
                    .join('\n');

                if (data) {
                    onEvent(JSON.parse(data) as AiAssistantStreamEvent);
                }
            }
        }
    }

    private async _validToken(): Promise<string> {
        const token = this._authService.accessToken;
        if (!token || AuthUtils.isTokenExpired(token, REFRESH_MARGIN_SECONDS)) {
            await firstValueFrom(this._authService.refreshToken());
        }
        return this._authService.accessToken;
    }
}
